"use client";

import { RefObject, useEffect, useState } from "react";
import { ImSpinner2 } from "react-icons/im";

import { usePlayBackStore } from "~/stores/playBackStore";

export default function PlayBack({
  id,
  audioRef,
  isPlaying,
  onPlay,
  hasAudio,
}: {
  id: number;
  audioRef: RefObject<HTMLAudioElement>;
  isPlaying: boolean;
  onPlay: () => void;
  hasAudio: boolean;
}) {
  const [isAudioPlaying, setIsAudioPlaying] = useState(isPlaying);
  const [progress, setProgress] = useState(0);

  const isLoading = usePlayBackStore(
    (state) => state.loadingStates[id] || false,
  );

  useEffect(() => {
    const audioElement = audioRef.current;

    const handleTimeUpdate = () => {
      if (audioElement?.duration) {
        const currentProgress =
          (audioElement.currentTime / audioElement.duration) * 100;
        setProgress(currentProgress);
      }
    };

    const handleAudioEnd = () => {
      setIsAudioPlaying(false);
      setProgress(0);
      if (audioElement) {
        audioElement.currentTime = 0;
      }
    };

    if (audioElement) {
      audioElement.addEventListener("timeupdate", handleTimeUpdate);
      audioElement.addEventListener("ended", handleAudioEnd);
    }

    return () => {
      if (audioElement) {
        audioElement.removeEventListener("timeupdate", handleTimeUpdate);
        audioElement.removeEventListener("ended", handleAudioEnd);
      }
    };
  }, [audioRef]);

  useEffect(() => {
    setIsAudioPlaying(isPlaying);
  }, [isPlaying]);

  const togglePlayPause = () => {
    if (isAudioPlaying) {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      setIsAudioPlaying(false);
    } else {
      if (audioRef.current?.paused) {
        if (
          audioRef.current.currentTime > 0 &&
          audioRef.current.src.includes(id.toString())
        ) {
          void audioRef.current.play();
        } else {
          onPlay();
        }
      } else {
        onPlay();
      }
      setIsAudioPlaying(true);
    }
  };

  return (
    <>
      {isLoading ? (
        <div className="flex animate-pulse items-center space-x-2">
          <ImSpinner2 className="size-5 animate-spin text-gray-300" />
          <div className="h-2 w-1/5 rounded bg-gray-300"></div>
        </div>
      ) : (
        hasAudio && (
          <>
            <div className="flex items-center space-x-2">
              <button
                onClick={togglePlayPause}
                className="text-gray-800 duration-150 active:text-gray-400"
              >
                {isAudioPlaying ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="size-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M10 6.75v10.5m3.75-10.5v10.5"
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="size-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15.91 11.672a.375.375 0 0 1 0 .656l-5.603 3.113a.375.375 0 0 1-.557-.328V8.887c0-.286.307-.466.557-.327l5.603 3.112Z"
                    />
                  </svg>
                )}
              </button>
              {isPlaying ? (
                <div className="w-1/5">
                  <div className="overflow-hidden rounded-full bg-gray-200">
                    <div
                      style={{
                        width: `${progress}%`,
                      }}
                      className="h-2 rounded-full bg-gray-600 duration-500 ease-linear"
                    />
                  </div>
                </div>
              ) : (
                <div className="w-1/5">
                  <div className="overflow-hidden rounded-full bg-gray-200">
                    <div
                      style={{
                        width: "0%",
                      }}
                      className="h-2 rounded-full bg-gray-600 duration-500 ease-linear"
                    />
                  </div>
                </div>
              )}
            </div>
          </>
        )
      )}
    </>
  );
}
